import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ChatExchange } from './chat-exchange/chat-exchange';
import { Router } from '@angular/router';
import { ChatService } from '../services/chat-service/chat.service';
import { Message } from '../services/chat-service/types';
import { formatISO } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { provideIcons } from '@ng-icons/core';
import { tablerShare2 } from '@ng-icons/tabler-icons';
import { ChatPrompt } from '../shared/chat-prompt/chat-prompt';
import { NeedHelpService } from '../services/need-help.service';

@Component({
  selector: 'chat-page',
  templateUrl: './chat-page.html',
  styleUrl: './chat-page.scss',
  imports: [MatButtonModule, ChatExchange, ChatPrompt],
  providers: [provideIcons({ tablerShare2 })],
})
export class ChatPage {
  private router = inject(Router);
  private chatService = inject(ChatService);
  private needHelp = inject(NeedHelpService);

  userInput = signal('');

  // from route params
  readonly chatId = input.required<string>();

  readonly chatDetail = this.chatService.getChatDetail(this.chatId);
  readonly displayTitle = computed(() => {
    return this.chatDetail.value()?.name || '';
  });
  readonly messageList = signal<Message[]>([]);

  sendMessage = (query: string) => {
    // Append the user query to messageList while waiting for the bot response
    this.messageList.update((prev) => {
      const newUserMessage: Message = {
        id: uuidv4(),
        created_at: formatISO(new Date()),
        role: 'user',
        content: query,
        recommended_assets: [],
      };
      return prev.concat(newUserMessage);
    });

    // Generate a unique id for the response so that we can identify it
    // across the entire process of rendering the response. The message is created
    // right away (empty, with no steps yet) so the "Thinking…" indicator shows
    // while the backend works.
    const newAssistantResponseMessageId = uuidv4();
    this.messageList.update((prev) =>
      prev.concat({
        id: newAssistantResponseMessageId,
        created_at: formatISO(new Date()),
        role: 'assistant',
        content: '',
        recommended_assets: [],
        steps: [],
        thinkEvents: [],
      }),
    );
    const updateResponse = (fn: (msg: Message) => Message) =>
      this.messageList.update((prev) =>
        prev.map((msg) => (msg.id === newAssistantResponseMessageId ? fn(msg) : msg)),
      );

    this.chatService.sendMessage(this.chatId(), query).subscribe({
      next: (res) => {
        if (res.event === 'chunk') {
          updateResponse((msg) => ({ ...msg, content: msg.content + res.data }));
        } else if (res.event === 'data') {
          updateResponse((msg) => ({ ...msg, recommended_assets: res.data.assets }));
        } else if (res.event === 'think') {
          const step = res.data;
          updateResponse((msg) => ({
            ...msg,
            steps: [...(msg.steps ?? []).filter((s) => !step.id || s.id !== step.id), step],
            thinkEvents: [...(msg.thinkEvents ?? []), step],
          }));
        } else if (res.event === 'error') {
          updateResponse((msg) => ({ ...msg, content: msg.content || res.data }));
        } else if (res.event === 'navigate') {
          if (res.data === 'catalog') {
            this.router.navigateByUrl('/catalog');
          } else {
            this.needHelp.open();
          }
        }
      },
      error: () =>
        updateResponse((msg) => ({
          ...msg,
          content: msg.content || 'Something went wrong generating a response.',
        })),
    });
  };

  constructor() {
    const newChatQuery = this.router.currentNavigation()?.extras.state?.['userQuery'];

    effect(() => {
      if (this.chatDetail.hasValue()) {
        const messageCount = this.chatDetail.value().messages.length;

        // We have to check whether there are any existing messages in this chat,
        // because the user might refresh the page with the first message still in
        // the navigation state which we no longer need.
        if (messageCount === 0) {
          // If there are no messages, send the first message and append the response.
          this.sendMessage(newChatQuery);
        } else {
          // Otherwise we take the message history and use that as the starting state.
          this.messageList.set(this.chatDetail.value().messages);
        }
      }
    });
  }
}
